import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { words } from '../translations'

const CustomDropdown = ({ id, listId, value, options, onSelect,questionLength,language, groupTitles, allowOpenEnded, openEndedName, openEndedNameFr, onOpenEndedClick }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [activeGroup, setActiveGroup] = useState(null);
  const wrapperRef = useRef(null);

  const handleSelect = (option) => {
    setSelected(option);
    setOpen(false);
    setActiveGroup(null);
    onSelect(option);
  };

  useEffect(() =>{
    if (value) {
      setSelected(value)
    } else {
      setSelected('')
    }
  },[value])
 
  useEffect(() =>{
    if(!open){
      setActiveGroup(null)
    }
  },[open])

  // Close on click-away and ESC key
  useEffect(() => {
    if (!open) return; // only attach when open

    const handleClickAway = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setActiveGroup(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        setOpen(false);
        setActiveGroup(null);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  
  const getOptionName = (option) => {
    if (language === 'FR' && option?.fr) {
      return option.fr
    } 
    if (language === 'ES' && option?.es) {
      return option.es
    }
    return option.name

  
  }

  const getGroupTitle = (groupKey) => {
    if(!groupTitles) return groupKey
    const localized = groupTitles?.[groupKey]
    return localized?.[language] || localized?.EN || groupKey
  }

  const groupKeys = useMemo(() => groupTitles ? Object.keys(groupTitles) : [], [groupTitles])

  const optionsByGroup = useMemo(() => {
    if(!groupKeys.length) return {}
    const map = {}
    groupKeys.forEach(k => { map[k] = [] })
    if (options) {
      options.forEach(opt => {
        if(opt?.group && map.hasOwnProperty(opt.group)){
          map[opt.group].push(opt)
        }
      })
    }
    return map
  }, [options, groupKeys])

  const ungroupedOptions = useMemo(() => {
    return options?.filter(opt => !opt?.group) || []
  }, [options])


  const getSelectedName = (selectedOption) => {
    if (selectedOption === 'open_ended') {
      return language === 'FR' ? openEndedNameFr : openEndedName
    }
    if (language === 'FR' && selectedOption?.fr) {
      return selectedOption.fr
    } 
    if (language === 'ES' && selectedOption?.es) {
      return selectedOption.es
    }
    return selectedOption.name
  }



  return (
    <DropdownWrapper ref={wrapperRef}>
    <DropdownButton onClick={() => setOpen(!open)} id={id} hasValue={!!selected}>
      <ButtonText>{getSelectedName(selected) || (language === 'FR' ? 'Sélectionner...' : 'Select...')}</ButtonText>
      <Chevron open={open} />
    </DropdownButton>
    <DropdownContent open={open} id={listId} questionLength={questionLength}>
      {activeGroup === null ? (
        <>
          {groupKeys.map((g) => (
            <DropdownItem key={`group_${g}`} onClick={() => setActiveGroup(g)}>
              <span>{getGroupTitle(g)}</span>
              <ItemChevronRight />
            </DropdownItem>
          ))}
          {groupKeys.length > 0 && ungroupedOptions.map((option, index) => (
            <DropdownItem key={`opt_${index}_${option?.id || option?.name}`} onClick={() => handleSelect(option)}>
              {getOptionName(option)}
            </DropdownItem>
          ))}
          {!groupKeys.length && options?.map((option, index) => (
            <DropdownItem key={`flat_${index}_${option?.id || option?.name}`} onClick={() => handleSelect(option)}>
              {getOptionName(option)}
            </DropdownItem>
          ))}
          {allowOpenEnded && (
            <DropdownItem key="open_ended" onClick={() => { setOpen(false); setActiveGroup(null); onOpenEndedClick(); }}>
              {language === 'FR' ? openEndedNameFr : openEndedName}
            </DropdownItem>
          )}
        </>
      ) : (
        <>
          <BackItem onClick={() => setActiveGroup(null)}>
            <BackChevron />
            <span>{words['back']?.[language] || 'Back'}</span>
          </BackItem>
          {(optionsByGroup[activeGroup] || []).map((option, index) => (
            <DropdownItem key={`grp_${activeGroup}_${index}_${option?.id || option?.name}`} onClick={() => handleSelect(option)}>
              {getOptionName(option)}
            </DropdownItem>
          ))}
          {allowOpenEnded && (
            <DropdownItem key="open_ended_grouped" onClick={() => { setOpen(false); setActiveGroup(null); onOpenEndedClick(); }}>
              {language === 'FR' ? openEndedNameFr : openEndedName}
            </DropdownItem>
          )}
        </>
      )}
    </DropdownContent>
  </DropdownWrapper>
  )
};

export default CustomDropdown;

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  font-family: 'Raleway';
  margin-bottom: 16px;
`;

const DropdownButton = styled.button`
  background-color: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  font-size: 14px;
  outline: none;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: #cbd5e1;
    background-color: #f8fafc;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:active {
    transform: scale(0.995);
  }
`;

const ButtonText = styled.span`
  flex: 1;
  color: ${({ children }) => (children?.[0] && !children[0].toString().includes('Select') && !children[0].toString().includes('Sélectionner')) ? '#1a202c' : '#94a3b8'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownContent = styled.div`
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: absolute;
  background-color: white;
  min-width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10;
  max-height: 260px;
  overflow-y: auto;
  
  @media (max-height: 775px) {
    max-height:210px;
  }

  @media (max-height: 700px) {
    max-height:180px;
  }

  @media (max-height: 650px) {
    max-height:160px;
  }

  @media (max-height: 600px) {
    max-height:120px;
  }

  /* Smooth scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const DropdownItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #334155;
  transition: all 0.1s ease-in-out;
  
  &:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }
  
  &:last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  
  &:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }

  &:active {
    background-color: #e2e8f0;
  }
`;

const Chevron = styled.i`
  border: solid #64748b;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  margin-left: auto;
  flex-shrink: 0;
  transform: ${({ open }) => (open ? 'rotate(-135deg)' : 'rotate(45deg)')};
  transition: transform 0.2s ease-in-out;
`;

const BackItem = styled.div`
  padding: 10px 16px;
  cursor: pointer;
  color: #475569;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1.5px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.1s ease-in-out;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  
  &:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }

  &:active {
    background-color: #e2e8f0;
  }
`;

const ItemChevronRight = styled.i`
  border: solid #64748b;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(-45deg);
`;

const BackChevron = styled.i`
  border: solid #64748b;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(135deg);
`;
